import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { PlusIcon } from './icons';
import { FaTrash } from 'react-icons/fa';
import { ArrowLeftIcon, ArrowRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ButtonFilled from './common/button/buttonFilled';
import ButtonOutlined from './common/button/buttonOutlined';

import { ProfileContext } from '../contexts/profileContext';

const CaptionsEditor = ({
  subtitleContent,
  setSubtitleContent,
  initialCues = [],
  onSubmit,
  setCurrentVideoTime,
  setPreviewVttContent,
  showSimpleEditor,
  textVttContent
}) => {
  const { t } = useTranslation();
  const [cues, setCues] = useState([]);
  const [errors, setErrors] = useState({});
  const [simpleEditorMode, setSimpleEditorMode] = useState(false);
  const [simpleEditorText, setSimpleEditorText] = useState('');

  const [profile] = useContext(ProfileContext);
  const { masterOrganization: { customConfigurations: { colors } = {} } = {} } = profile;

  useEffect(() => {
    if (showSimpleEditor) {
      setSimpleEditorMode(true);
    }
  }, [showSimpleEditor]);

  useEffect(() => {
    if (initialCues && initialCues.length > 0) {
      setCues(
        initialCues.map((cue, index) => ({
          ...cue,
          identifier: String(index + 1)
        }))
      );
    }
  }, [initialCues]);

  useEffect(() => {
    if (textVttContent) {
      setSimpleEditorText(textVttContent);
    }
  }, [textVttContent]);

  const validateCue = (cue, index) => {
    const newErrors = {};

    if (!cue.text || cue.text.trim() === '') {
      newErrors.text = t('captionsEditor.textRequired');
    }

    if (cue.start < 0) {
      newErrors.start = t('captionsEditor.startTimeMustBePositive');
    }

    if (cue.end <= cue.start) {
      newErrors.end = t('captionsEditor.endTimeMustBeGreaterThanStartTime');
    }

    // Check for overlapping with other cues
    const otherCues = cues.filter((_, i) => i !== index);
    const hasOverlap = otherCues.filter(
      (otherCue) =>
        (otherCue.identifier !== cue.identifier &&
          cue.start >= otherCue.start &&
          cue.start < otherCue.end) ||
        (cue.end > otherCue.start && cue.end <= otherCue.end) ||
        (cue.start <= otherCue.start && cue.end >= otherCue.end)
    );

    if (hasOverlap.length > 0) {
      newErrors.timing = `${t('captionsEditor.cueOverlapsWithAnotherCue')} ${hasOverlap
        .map((cue) => cue.identifier)
        .join(', ')}`;
    }

    return newErrors;
  };

  const updateCue = (index, field, value) => {
    const newCues = [...cues];
    newCues[index] = { ...newCues[index], [field]: value };
    setCues(newCues);

    // Clear errors for this cue when editing
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const addCue = (insertIndex = cues.length) => {
    const newCue = {
      identifier: '',
      start: insertIndex > 0 ? cues[insertIndex - 1].end : 0,
      end: insertIndex > 0 ? cues[insertIndex - 1].end + 0.5 : 0.5,
      text: '',
      styles: ''
    };

    const newCues = [...cues];
    newCues.splice(insertIndex, 0, newCue);

    // Reindex all cues
    const reindexedCues = newCues.map((cue, index) => ({
      ...cue,
      identifier: String(index + 1)
    }));

    setCues(reindexedCues);
  };

  const removeCue = (index) => {
    const newCues = cues.filter((_, i) => i !== index);

    // Reindex remaining cues
    const reindexedCues = newCues.map((cue, index) => ({
      ...cue,
      identifier: String(index + 1)
    }));

    setCues(reindexedCues);

    // Clear errors for removed cue
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(
      secs
    ).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
  };

  const handleTimeInputWithArrowKeys = (e, index, field) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      adjustTime(index, field, -0.1);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      adjustTime(index, field, 0.1);
    }
  };

  const adjustTime = (index, field, delta) => {
    const currentValue = cues[index][field];
    const newValue = Math.max(0, Math.round((currentValue + delta) * 1000) / 1000);
    updateCue(index, field, newValue);
  };

  const handleSubmit = ({ preview = false }) => {
    const submitFunction = preview ? setPreviewVttContent : onSubmit;

    if (simpleEditorMode) {
      submitFunction(simpleEditorText);
      return;
    }

    // Validate all cues
    const allErrors = {};
    let hasErrors = false;

    cues.forEach((cue, index) => {
      const cueErrors = validateCue(cue, index);
      if (Object.keys(cueErrors).length > 0) {
        allErrors[index] = cueErrors;
        hasErrors = true;
      }
    });

    setErrors(allErrors);

    if (!hasErrors) {
      const result = {
        valid: true,
        strict: true,
        cues: cues,
        errors: []
      };
      submitFunction(result);
    }
  };

  useEffect(() => {
    if (!setPreviewVttContent) return;

    handleSubmit({ preview: true });
  }, [cues, simpleEditorText]);

  useEffect(() => {
    setSubtitleContent((prev) => ({ ...prev, unsavedChanges: !_.isEqual(initialCues, cues) }));
  }, [cues]);

  if (simpleEditorMode) {
    return (
      <div className="w-full">
        <div className="flex flex-col gap-4 mb-6">
          <textarea
            name="simpleEditorText"
            value={simpleEditorText}
            onChange={(e) => setSimpleEditorText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="20"
            placeholder="Enter caption text..."
          />
          <div className="flex justify-start gap-3 mt-6 pt-4">
            <ButtonFilled
              onClick={handleSubmit}
              className="px-8 py-4 !bg-black text-white !rounded-full !hover:bg-gray-800 transition-colors">
              <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.saveCaptions') }} />
            </ButtonFilled>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {cues.length === 0 && (
        <div className="flex flex-col gap-6 justify-center items-center mt-12 mb-6">
          <ButtonOutlined
            onClick={() => addCue()}
            className="px-4 py-2 flex items-center gap-1 transition-all hover:scale-105 active:scale-95">
            <PlusIcon className="w-8 h-8" color={colors?.primary} />
            <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.addCue') }} />
          </ButtonOutlined>
          <ButtonOutlined
            onClick={() => setSimpleEditorMode(true)}
            className="px-4 py-2 flex items-center gap-1 transition-all hover:scale-105 active:scale-95">
            <DocumentTextIcon className="w-8 h-8" color={colors?.primary} />
            <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.simpleEditor') }} />
          </ButtonOutlined>
        </div>
      )}

      <div className="space-y-4 overflow-y-auto max-h-[40dvh]">
        {cues.map((cue, index) => (
          <div key={index}>
            <div className="flex w-full justify-center">
              <button
                onClick={() => addCue(index)}
                className="text-xs p-1 bg-gray-100 text-gray-500 rounded-full hover:bg-gray-200 cursor-pointer my-2"
                title="Insert cue before this one">
                <PlusIcon />
              </button>
            </div>
            <div className="relative border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex gap-2">
                <div className="flex justify-between items-start mb-3 w-full">
                  <span className="text-sm font-medium text-gray-600">
                    <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.cue') }} /> #
                    {cue.identifier}
                  </span>
                  <button
                    onClick={() => removeCue(index)}
                    className="text-xs px-2 py-1 text-red-500 hover:text-red-600 cursor-pointer"
                    disabled={cues.length === 1}>
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div className="relative">
                  <label
                    htmlFor={`start-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1">
                    <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.startTime') }} />{' '}
                    (hh:mm:ss.SSS)
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 h-full flex items-center justify-center bg-gray-300 hover:bg-gray-600 hover:text-white transition-all rounded-l-md">
                      <button
                        className="cursor-pointer w-8 p-2"
                        onClick={() => adjustTime(index, 'start', -0.1)}>
                        <ArrowLeftIcon />
                      </button>
                    </div>
                    <input
                      tabIndex={1}
                      type="text"
                      id={`start-${index}`}
                      value={formatTime(cue.start)}
                      // onChange={(e) => updateCue(index, 'start', e.target.value)}
                      // onBlur={(e) => updateCue(index, 'start', parseTime(e.target.value))}
                      onKeyDown={(e) => handleTimeInputWithArrowKeys(e, index, 'start')}
                      className="w-full px-9 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      placeholder="00:00:00.000"
                      readOnly={true}
                    />
                    <div className="absolute right-0 top-0 h-full flex items-center justify-center bg-gray-300 hover:bg-gray-600 hover:text-white transition-all rounded-r-md">
                      <button
                        className="cursor-pointer w-8 p-2"
                        onClick={() => adjustTime(index, 'start', 0.1)}>
                        <ArrowRightIcon />
                      </button>
                    </div>
                  </div>
                  {errors[index]?.start && (
                    <p className="text-red-500 text-xs mt-1">{errors[index].start}</p>
                  )}
                </div>

                <div className="relative">
                  <label
                    htmlFor={`end-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1">
                    <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.endTime') }} />{' '}
                    (hh:mm:ss.SSS)
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 h-full flex items-center justify-center bg-gray-300 hover:bg-gray-600 hover:text-white transition-all rounded-l-md">
                      <button
                        className="cursor-pointer w-8 p-2"
                        onClick={() => adjustTime(index, 'end', -0.1)}>
                        <ArrowLeftIcon />
                      </button>
                    </div>
                    <input
                      tabIndex={1}
                      type="text"
                      id={`end-${index}`}
                      value={formatTime(cue.end)}
                      // onChange={(e) => updateCue(index, 'end', e.target.value)}
                      // onBlur={(e) => updateCue(index, 'end', parseTime(e.target.value))}
                      onKeyDown={(e) => handleTimeInputWithArrowKeys(e, index, 'end')}
                      className="w-full px-9 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      placeholder="00:00:00.000"
                      readOnly={true}
                    />
                    <div className="absolute right-0 top-0 h-full flex items-center justify-center bg-gray-300 hover:bg-gray-600 hover:text-white transition-all rounded-r-md">
                      <button
                        className="cursor-pointer w-8 p-2"
                        onClick={() => adjustTime(index, 'end', 0.1)}>
                        <ArrowRightIcon />
                      </button>
                    </div>
                  </div>
                  {errors[index]?.end && (
                    <p className="text-red-500 text-xs mt-1">{errors[index].end}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`duration-${index}`}
                    className="block text-sm font-medium text-gray-700 mb-1">
                    <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.duration') }} />{' '}
                    (hh.mm.ss.SSS)
                  </label>
                  <input
                    type="text"
                    id={`duration-${index}`}
                    value={formatTime(cue.end - cue.start)}
                    readOnly={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                </div>
              </div>

              {errors[index]?.timing && (
                <p className="text-red-500 text-xs mb-2">{errors[index].timing}</p>
              )}

              <div>
                <label
                  htmlFor={`text-${index}`}
                  className="block text-sm font-medium text-gray-700 mb-1">
                  <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.captionText') }} />
                </label>
                <textarea
                  tabIndex={1}
                  id={`text-${index}`}
                  onClick={() => setCurrentVideoTime(cue.start + 0.2)}
                  value={cue.text}
                  onChange={(e) => updateCue(index, 'text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Enter caption text..."
                  // maxLength={35}
                />
                <div className="flex justify-end">
                  <p className="text-xs text-gray-500 mt-1">{cue.text.length}</p>
                </div>
                {errors[index]?.text && (
                  <p className="text-red-500 text-xs mt-1">{errors[index].text}</p>
                )}
              </div>
            </div>
            {index === cues.length - 1 && (
              <div className="flex w-full justify-center">
                <button
                  onClick={() => addCue(index + 1)}
                  className="text-xs p-1 bg-gray-100 text-gray-500 rounded-full hover:bg-gray-200 cursor-pointer mt-6"
                  title="Insert cue after this one">
                  <PlusIcon />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {cues.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>
            <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.noCaptions') }} />
          </p>
        </div>
      )}

      <div className="flex justify-start gap-3 mt-6 pt-4">
        <ButtonFilled
          onClick={handleSubmit}
          className="px-8 py-4 !bg-black text-white !hover:bg-gray-800 transition-colors"
          disabled={cues.length === 0 || Object.keys(errors).length > 0}>
          <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.saveCaptions') }} />
        </ButtonFilled>
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-700 text-sm font-medium">
            <span dangerouslySetInnerHTML={{ __html: t('captionsEditor.pleaseFixErrors') }} />
          </p>
        </div>
      )}
    </div>
  );
};

export default CaptionsEditor;
